import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

// --- Importação de Componentes ---
import GanttChart from "./GanttChart";
import GanttFinanceiroProjeto from "./GanttFinanceiroProjeto";
import ModalSolicitarAditivo from "./ModalSolicitarAditivo";
import ModalAuditoria from "./ModalAuditoria";
import ModalRelatorios from "./viewrelatorios";
import ModalDocumentos from "./modalDocs";
import GraficoEconomicidade from "./GraficoEconomicidade";
import { getUserRole, getUserSecretariaId, fetchToken } from "./Auth";

// --- Importação do CSS Novo ---
import "../css/viewProjeto.css";

function ViewProjetoCompleto() {
  const { id } = useParams();

  // Estados de Dados
  const [projeto, setProjeto] = useState(null);
  const [cronogramaData, setCronogramaData] = useState([]);
  const [valorGasto, setValorGasto] = useState(0);

  // Estados de UI/Controle
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados dos Modais
  const [showModal, setShowModal] = useState(false);
  const [showRelatorioModal, setShowRelatorioModal] = useState(false);
  const [showDocumentosModal, setShowDocumentosModal] = useState(false);

  // Dados do Usuário
  const userRole = getUserRole();
  const userSecretariaId = getUserSecretariaId();
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null);

  // --- Efeito: Carregar Dados ---
  useEffect(() => {
    const fetchDadosCompletos = async () => {
      const token = fetchToken();

      // Validação de Token
      if (!token) {
        setError("Sessão inválida. Faça login novamente.");
        setLoading(false);
        return;
      }

      try {
        // 1. Busca dados do projeto
        const resProjeto = await axios.get(
          `/api/projetocompleto/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const dataProjeto = resProjeto.data;

        if (dataProjeto.Status === "Success" && dataProjeto.Result.length > 0) {
          const p = dataProjeto.Result[0];
          setProjeto(p);

          try {
            // 2. Busca cronograma e calcula gastos
            const resCronograma = await axios.get(
              `/api/cronograma/${id}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            );

            const cronogramaItens = resCronograma.data || [];
            setCronogramaData(cronogramaItens);

            const somaTotalGasto = cronogramaItens.reduce((accItens, item) => {
              const parcelas = item.mensal || [];
              const somaParcelasDoItem = parcelas.reduce(
                (accParcelas, parcela) => {
                  return accParcelas + parseFloat(parcela.valor_simples || 0);
                },
                0,
              );
              return accItens + somaParcelasDoItem;
            }, 0);

            setValorGasto(somaTotalGasto);
          } catch (cronogramaErr) {
            console.error("Erro ao buscar cronograma:", cronogramaErr.message);
            setValorGasto(0);
            setCronogramaData([]);
          }
        } else {
          setError("Projeto não encontrado ou sem permissão de visualização.");
        }
      } catch (err) {
        setError("Erro ao carregar dados do projeto (401/403).");
        console.error(err);
      } finally {
        setLoading(false);
      }

      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          setUser(payload);
        } catch (e) {
          console.error("Erro token:", e);
        }
      }
    };

    fetchDadosCompletos();
  }, [id]);

  // --- Lógicas de Cálculo ---

  const calcularProgressoEtapa = (inicio, fim, situacao) => {
    if (situacao === "Concluído") return 100;
    if (situacao === "Não iniciado") return 0;
    if (!inicio || !fim) return 0;

    const hoje = new Date();
    const dataInicio = new Date(`${inicio}T00:00:00`);
    const dataFim = new Date(`${fim}T23:59:59`);

    if (situacao === "Em andamento") {
      if (hoje < dataInicio) return 0;
      if (hoje > dataFim) return 100;

      const totalDias = (dataFim - dataInicio) / (1000 * 60 * 60 * 24);
      const diasPassados = (hoje - dataInicio) / (1000 * 60 * 60 * 24);

      if (totalDias <= 0) return 100;

      return Math.round((diasPassados / totalDias) * 100);
    }
    return 0;
  };

  const progresso = () => {
    if (!projeto) return 0;
    const etapas = [1, 2, 3, 4, 5, 6];
    const soma = etapas.reduce((acc, n) => {
      return (
        acc +
        calcularProgressoEtapa(
          projeto[`data_inicio_etapa${n}`],
          projeto[`data_termino_etapa${n}`],
          projeto[`situacao_etapa${n}`],
        )
      );
    }, 0);
    return Math.round(soma / etapas.length);
  };

  const etapasAtrasadas = () => {
    if (!projeto) return [];
    const hoje = new Date();
    return [1, 2, 3, 4, 5, 6]
      .filter((n) => {
        const fim = projeto[`data_termino_etapa${n}`];
        const situacao = projeto[`situacao_etapa${n}`];
        const dataFim = new Date(`${fim}T23:59:59`);
        return situacao === "Em andamento" && hoje > dataFim;
      })
      .map((n) => ({
        etapa: projeto[`etapa${n}`] || `Etapa ${n}`,
        numero: n,
      }));
  };

  // NOVA LÓGICA: Define o valor total com base no valor licitado (se existir e for > 0)
  const valorTotalBase =
    parseFloat(projeto?.valor_licitado) > 0
      ? parseFloat(projeto.valor_licitado)
      : parseFloat(projeto?.valor_total_projeto) || 0;

  const restante = () => Math.max(0, valorTotalBase - valorGasto);

  const percentualGasto = () => {
    return valorTotalBase ? Math.round((valorGasto / valorTotalBase) * 100) : 0;
  };

  const statusFinal = (prog) => {
    const base =
      prog === 0 ? "Não iniciado" : prog < 100 ? "Em andamento" : "Concluído";
    const limite = new Date(`${projeto?.data_termino_etapa6}T23:59:59`);
    const hoje = new Date();
    return hoje > limite && prog < 100 ? "Atrasado" : base;
  };

  // --- Variáveis Calculadas ---
  const progAtual = progresso();
  const atrasadas = etapasAtrasadas();
  const statusAtual = statusFinal(progAtual);

  // --- Variáveis de Estilo Dinâmico ---
  const isAtrasado = atrasadas.length > 0 || statusAtual === "Atrasado";

  const corProgresso = isAtrasado
    ? "var(--red-danger)"
    : progAtual === 100
      ? "var(--green-success)"
      : "var(--blue-primary)";

  const statusPillClass = isAtrasado
    ? "danger"
    : progAtual === 100
      ? "success"
      : "primary";
  const kpiCardBorderClass = isAtrasado ? "kpi-danger" : "";

  // --- Função PDF ---
  const handleGerarPDF = async () => {
    const token = fetchToken();
    try {
      const response = await fetch("/api/gerar-pdf", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: [id] }),
      });

      if (!response.ok) throw new Error("Erro ao gerar PDF");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "relatorio_projetos.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      alert("Erro ao gerar o PDF do projeto");
    }
  };

  // --- Renderização Condicional ---
  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
        <div className="text-center">
          <div className="spinner-border text-primary mb-2" role="status" />
          <h5 className="text-muted">Carregando dados do projeto...</h5>
        </div>
      </div>
    );

  if (error || !projeto)
    return (
      <div className="container mt-5">
        <div className="alert alert-danger shadow-sm border-0">
          <h4 className="alert-heading">Acesso Negado ou Erro</h4>
          <p>{error || "Projeto não pôde ser carregado."}</p>
          <Link to="/projetocompleto" className="btn btn-outline-danger">
            Voltar
          </Link>
        </div>
      </div>
    );

  // --- Renderização Principal ---
  return (
    <div className="dashboard-container-v2">
      {/* Header */}
      <div className="header-dark-v2">
        <div>
          <Link to="/projetocompleto" className="btn-back-header">
            <i className="fas fa-arrow-left me-2" /> Voltar para Projetos
          </Link>
          <h1 className="header-title-v2">{projeto.nomeprojeto}</h1>
          <div className="header-meta-v2">
            <i className="fas fa-building me-1"></i> {projeto.secretaria_nome}
            <span className="mx-2">|</span>
            <i className="fas fa-user me-1"></i> {projeto.gestor}
          </div>
        </div>

        <div>
          <span className={`status-pill ${statusPillClass}`}>
            {statusAtual}
          </span>
        </div>
      </div>

      <div className="main-content-v2">
        {/* Barra de Ações */}
        <div className="action-bar-v2">
          <Link
            to={`/detalhesexecucao/${projeto.id}`}
            className="btn-v2 btn-v2-primary"
          >
            <i className="fas fa-tasks"></i> Detalhes da Execução
          </Link>

          <Link
            to={`/estatisticasprojeto/${projeto.id}`}
            className="btn-v2 btn-v2-outline"
          >
            <i className="fas fa-chart-pie"></i> Estatísticas
          </Link>

          <button onClick={handleGerarPDF} className="btn-v2 btn-v2-outline">
            <i className="fas fa-file-pdf"></i> PDF
          </button>

          <div
            style={{
              width: "1px",
              height: "30px",
              background: "#e0e0e0",
              margin: "0 10px",
            }}
            className="d-none d-md-block"
          ></div>

          <button
            onClick={() => setShowDocumentosModal(true)}
            className="btn-v2 btn-v2-outline"
          >
            <i className="fas fa-folder-open"></i> Documentos
          </button>

          <button
            onClick={() => setShowRelatorioModal(true)}
            className="btn-v2 btn-v2-outline"
          >
            Histórico Relatórios
          </button>

          <Link
            to={`/relatorio/${projeto.id}`}
            className="btn-v2 btn-v2-outline"
          >
            <i className="fas fa-plus"></i> Novo Relatório
          </Link>

          <div className="ms-auto d-flex gap-2">
            {userRole === "auditor" && (
              <button
                className="btn-v2 btn-v2-danger-outline"
                onClick={() => setShowModal(true)}
              >
                <i className="fas fa-search me-1"></i> Auditoria
              </button>
            )}

            {(userSecretariaId === projeto?.id_secretaria ||
              userRole === "admin") &&
              userRole !== "auditor" && (
                <button
                  className="btn-v2 btn-v2-outline text-warning border-warning"
                  onClick={() => setShowModal(true)}
                >
                  <i className="fas fa-file-signature me-1"></i> Solicitar
                  Aditivo
                </button>
              )}
          </div>
        </div>

        {/* Alerta de Atraso */}
        {atrasadas.length > 0 && (
          <div className="alert-box-v2">
            <i className="fas fa-exclamation-triangle"></i>
            <div>
              <div className="alert-title">Cronograma em Atraso</div>
              <span>
                As seguintes etapas precisam de atenção imediata:
                {atrasadas.map((e) => (
                  <span
                    key={e.numero}
                    className="badge bg-danger ms-2 text-white"
                  >
                    {e.etapa}
                  </span>
                ))}
              </span>
            </div>
          </div>
        )}

        {/* Cards de KPIs */}
        <div className="row g-4 mb-4">
          <div className="col-md-4">
            <div className={`kpi-card-v2 ${kpiCardBorderClass}`}>
              <div>
                <span className="kpi-label">Progresso Físico do Processo</span>
                <div className="d-flex justify-content-between align-items-baseline">
                  <div className="kpi-value" style={{ color: corProgresso }}>
                    {progAtual}%
                  </div>
                </div>
                <div className="progress-container-v2">
                  <div
                    className="progress-bar-v2"
                    style={{
                      width: `${progAtual}%`,
                      backgroundColor: corProgresso,
                    }}
                  ></div>
                </div>
              </div>
              <div className="kpi-subtext d-flex justify-content-between">
                <span>Previsão Término:</span>
                <strong className="text-dark">
                  {projeto.data_termino_etapa6
                    ? new Date(projeto.data_termino_etapa6).toLocaleDateString(
                        "pt-BR",
                      )
                    : "ND"}
                </strong>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="kpi-card-v2">
              <div>
                <span className="kpi-label">Execução Financeira</span>
                <div className="kpi-value">
                  R${" "}
                  {valorGasto.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
              <div className="kpi-subtext d-flex justify-content-between align-items-center">
                {/* Rótulo dinâmico para indicar se está usando valor licitado ou total */}
                <span>
                  Orçamento{" "}
                  {parseFloat(projeto?.valor_licitado) > 0
                    ? "Licitado"
                    : "Total"}
                  :
                </span>
                <strong className="text-dark">
                  R${" "}
                  {valorTotalBase.toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </strong>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="kpi-card-v2">
              <div>
                <span className="kpi-label">Saldo Disponível</span>
                <div className="kpi-value text-success">
                  R${" "}
                  {restante().toLocaleString("pt-BR", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
              <div className="kpi-subtext">
                <div className="d-flex justify-content-between mb-1">
                  <span>Utilizado:</span>
                  <strong>{percentualGasto()}%</strong>
                </div>
                <div className="progress" style={{ height: "6px" }}>
                  <div
                    className="progress-bar bg-success"
                    style={{ width: `${percentualGasto()}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gráficos */}
        <div className="row">
          <div className="col-12">
            <div className="chart-section-v2">
              <h5 className="chart-header-v2">Cronograma Físico (Gantt)</h5>
              {!loading && cronogramaData.length > 0 ? (
                <GanttChart cronograma={cronogramaData} />
              ) : (
                <div className="alert alert-light text-center border">
                  Nenhum dado de cronograma disponível.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="chart-section-v2">
              <h5 className="chart-header-v2">Fluxo Financeiro</h5>
              <GanttFinanceiroProjeto
                projeto={projeto}
                valorGasto={valorGasto}
              />
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="chart-section-v2 mt-4">
            <h5 className="chart-header-v2">Economicidade da Licitação</h5>
            <GraficoEconomicidade projeto={projeto} />
          </div>
        </div>
      </div>

      {/* Modais */}
      {showRelatorioModal && (
        <ModalRelatorios
          projetoId={projeto.id}
          onClose={() => setShowRelatorioModal(false)}
        />
      )}
      {showDocumentosModal && (
        <ModalDocumentos
          projetoId={projeto.id}
          onClose={() => setShowDocumentosModal(false)}
        />
      )}

      {(() => {
        if (userRole === "auditor" && showModal) {
          return (
            <ModalAuditoria
              projetoId={projeto.id}
              onClose={() => setShowModal(false)}
            />
          );
        } else if (
          (userSecretariaId === projeto?.id_secretaria ||
            userRole === "admin") &&
          showModal &&
          userRole !== "auditor"
        ) {
          return (
            <ModalSolicitarAditivo
              projetoId={projeto.id}
              idSecretaria={projeto.id_secretaria}
              onClose={() => setShowModal(false)}
            />
          );
        }
        return null;
      })()}
    </div>
  );
}

export default ViewProjetoCompleto;
